import { Button, Drawer, Form, Input, Select, Space } from "antd";
import { ReactNode, useEffect } from "react";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SuccessModal from "../../../../components/common/SuccessModal";
import Loader from "../../../../components/ui/Loader";
import {
  useCreatePortalSubDepartmentMutation,
  useGetProtalAllDepartmentQuery,
  useGetProtalSingleSubDepartmentQuery,
  useUpdatePortalSubDepartmentMutation,
} from "../../../../features/portal/settings/settingsApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";

export interface ModalConfirmProps<
  TypeT =
    | "SUCCESS"
    | "ADD"
    | "UPDATE"
    | "VIEW"
    | "CONFIRM"
    | "CONFIRM_FOR_APPROVAL"
> {
  open: boolean;
  action?: boolean;
  buttonBgDeep?: boolean;
  handleClose: (val?: any) => void;
  type?: TypeT;
  data?: any;
  title?: ReactNode;
  subTitle?: ReactNode;
  buttonName?: string;
  loading?: boolean;
  children?: ReactNode;
  isViewOpen?: boolean;
}

export default function SubDepartmentAddModal({
  title,
  action,
  buttonName,
  buttonBgDeep,
  subTitle,
  handleClose,
  children,
  type,
  data,
  loading,
  isViewOpen,
  ...rest
}: ModalConfirmProps) {
  const [form] = Form.useForm();
  const {
    open: successOpen,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal();

  const [createPortalSubDepartment, { isLoading, isError, error, isSuccess }] =
    useCreatePortalSubDepartmentMutation();

  const [
    updatePortalSubDepartment,
    {
      isLoading: updatePortalSubDepartmentIsLoading,
      isError: updatePortalSubDepartmentIsError,
      error: updatePortalSubDepartmentError,
      isSuccess: updatePortalSubDepartmentIsSuccess,
    },
  ] = useUpdatePortalSubDepartmentMutation();

  const {
    data: getProtalSingleSubDepartmentData,
    isError: getProtalSingleSubDepartmentIsError,
    error: getProtalSingleSubDepartmentError,
    isLoading: getProtalSingleSubDepartmentIsLoading,
  } = useGetProtalSingleSubDepartmentQuery(data?.id, { skip: !data?.id });

  const {
    data: getPortalDepartmentData,
    isError: getPortalDepartmentIsError,
    error: getPortalDepartmentError,
  } = useGetProtalAllDepartmentQuery({
    params: {
      page: 1,
      limit: 1000,
      is_published: 1,
    },
  });

  useError(isError, error, "Designation create failed");
  useError(
    updatePortalSubDepartmentIsError,
    updatePortalSubDepartmentError,
    "Designation update failed"
  );
  useError(
    getProtalSingleSubDepartmentIsError,
    getProtalSingleSubDepartmentError,
    "Single designation get data failed"
  );
  useError(
    getPortalDepartmentIsError,
    getPortalDepartmentError,
    "Get all department data failed"
  );

  const onFinish = (values: any) => {
    const newData = {
      name: values?.name,
      parent_department: Number(values?.parent_department),
      from: 'organization'
    };
    if (data?.id) {
      updatePortalSubDepartment({
        data: newData,
        id: data?.id,
      });
    } else {
      createPortalSubDepartment(newData);
    }
  };

  const handleOkay = () => {
    handleClose();
  };

  useEffect(() => {
    if (data?.id && getProtalSingleSubDepartmentData?.data) {
      form.setFieldsValue({
        name: getProtalSingleSubDepartmentData?.data?.name,
        parent_department:
          getProtalSingleSubDepartmentData?.data?.department?.id,
      });
    }
  }, [data?.id, getProtalSingleSubDepartmentData?.data, form]);

  useEffect(() => {
    if (isSuccess) {
      form.resetFields();
      handleSuccessOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (updatePortalSubDepartmentIsSuccess) {
      form.resetFields();
      handleSuccessOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePortalSubDepartmentIsSuccess]);

  return (
    <>
      {successOpen?.type === "ADD" && (
        <SuccessModal
          open={successOpen?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={successOpen?.data}
          title={`${
            data?.id
              ? "Designation Edited Successfully"
              : "New Designation Added Successfully"
          }`}
        />
      )}

      <Drawer
        title={`${
          type === "UPDATE" ? "Edit" : type === "ADD" ? "Add" : "View"
        } Designation`}
        placement="right"
        width={480}
        className="right_site_drawer"
        onClose={handleClose}
        {...rest}
      >
        {data?.id && getProtalSingleSubDepartmentIsLoading ? (
          <Loader />
        ) : (
          <Form
            className="custom-form"
            layout="vertical"
            requiredMark="optional"
            size="large"
            onFinish={onFinish}
            form={form}
          >
            <Form.Item
              label="Designation Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: "Please enter designation name",
                },
              ]}
            >
              <Input
                placeholder="Enter Designation"
                allowClear
                disabled={type === "VIEW"}
              />
            </Form.Item>

            <Form.Item
              label="Department"
              name="parent_department"
              rules={[
                {
                  required: true,
                  message: "Please enter a department",
                },
              ]}
            >
              <Select
                placeholder="Department Name"
                showSearch
                options={
                  getPortalDepartmentData?.data?.departments?.map(
                    (department: any) => ({
                      label: department?.name,
                      value: department?.id,
                    })
                  ) || []
                }
                className="w-full"
                suffixIcon={<SelectDownIcon />}
                style={{ width: "100%" }}
                allowClear
                optionFilterProp="label"
                disabled={type === "VIEW"}
              />
            </Form.Item>

            {/* <Form.Item
              label="Head of Department"
              name="company_id"
              rules={[
                { required: false, message: "Please enter a company name" },
              ]}
            >
              <Select
                placeholder="Company Name"
                showSearch
                //   options={
                //     data?.data?.companies?.map((company: any) => ({
                //       label: company?.company_name,
                //       value: company?.id,
                //     })) || []
                //   }
                className="w-full"
                suffixIcon={<SelectDownIcon />}
                style={{ width: "100%" }}
                allowClear
                optionFilterProp="label"
              />
            </Form.Item> */}

            <Space className="w-full mt-2" size={12}>
              <Button
                onClick={handleClose}
                className="normal-dark-button modal-button"
                size="middle"
              >
                Cancel
              </Button>
              {type !== "VIEW" && (
                <Button
                  className="modal-button"
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  loading={isLoading || updatePortalSubDepartmentIsLoading}
                >
                  {data?.id ? "Update" : "Create"}
                </Button>
              )}
            </Space>
          </Form>
        )}
      </Drawer>
    </>
  );
}
